import clsx from 'clsx'
import { DEFAULT_TLS_PORT, TLS_PORT_NOTE, tlsPortProblem } from '@/data/tlsPort'
import { useInfrastructureWizard } from '@/stores/infrastructureWizard'
import type { ApplicationAnswers } from '@/types/application'
import { asksCreateFor, dbServerAddress, flagValues, licenseProductId, scriptInputs } from '@/utils/applicationDetails'
import { applicationProblems } from '@/utils/applicationInstall'
import { asksLicense } from '@/utils/infraValidation'
import page from '@/pages/NewDeploymentPage.module.css'
import styles from './Infrastructure.module.css'
import { ApplicationCreation } from './ApplicationCreation'
import { ApplicationInstallNote } from './ApplicationInstallNote'
import { ApplicationLicenseManagement } from './ApplicationLicenseManagement'
import { CreatedForQuestion } from './CreatedForQuestion'
import { LicenseFields } from './LicenseFields'

interface ApplicationSettingsProps {
  /** Traverse: the TLS port and the two database names are asked as well. */
  traverse: boolean
  tlsPort: string
  onTlsPortChange: (value: string) => void
  /** SYS + Customer ID, and the Customer ID, or '' until the Customer ID is valid. */
  sysDb: string
  mainDb: string
  /** Traverse: the answers to License management and Application creation. */
  answers: ApplicationAnswers
  onAnswersChange: (answers: ApplicationAnswers) => void
  /** From the Infrastructure step, for the script's inputs. */
  customerName: string
  productLineId: string
}

/**
 * The Application (ERP) step of a product line (Traverse, Process Pro): when the application is installed, and the license of the
 * environment's type (Site ID and key or keys). For Traverse also the TLS port, the SYS and Main database names, License management
 * (the License Server Manager's details) and Application creation (the set-up script's inputs). Whatever an earlier step already
 * answered is shown read-only, not asked again.
 */
export function ApplicationSettings({ traverse, tlsPort, onTlsPortChange, sysDb, mainDb, answers, onAnswersChange, customerName, productLineId }: ApplicationSettingsProps) {
  const data = useInfrastructureWizard()
  const problems = applicationProblems(data, { traverse, tlsPort, answers })
  const context = { infra: data, customerName, productLineId, tlsPort, sysDb, mainDb }
  const tlsError = traverse ? tlsPortProblem(tlsPort) : null

  return (
    <div className={page.networkSection} data-testid="application-settings">
      <ApplicationInstallNote />

      {traverse && (
        <div className={page.formGrid} style={{ marginTop: 16 }}>
          <div className={page.formGroup}>
            <label className={page.label} htmlFor="traverse-tls-port">
              TLS Port <span className={page.required}>*</span>
            </label>
            <input
              id="traverse-tls-port"
              className={clsx(page.input, tlsError !== null && tlsPort !== '' && page.inputError)}
              value={tlsPort}
              inputMode="numeric"
              aria-required="true"
              aria-invalid={tlsError !== null ? true : undefined}
              aria-describedby="traverse-tls-port-note"
              onChange={(event) => onTlsPortChange(event.target.value)}
              placeholder={String(DEFAULT_TLS_PORT)}
            />
            {tlsError !== null && tlsPort !== '' && (
              <span role="alert" className={page.errorMsg}>
                {tlsError}
              </span>
            )}
            <div id="traverse-tls-port-note" className={page.fieldHint}>
              {TLS_PORT_NOTE}
            </div>
          </div>
          <div className={page.formGroup}>
            <label className={page.label}>
              SYS DB name <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--aptean-text-muted)' }}>(auto-computed)</span>
            </label>
            <div className={page.vnetInfoBox}>
              {sysDb ? (
                <>
                  <span className={page.vnetName} style={{ fontFamily: 'monospace', fontSize: 13 }}>
                    {sysDb}
                  </span>
                  <span className={page.vnetHint}>SYS + Customer ID</span>
                </>
              ) : (
                <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--aptean-text-muted)' }}>SYS&lt;Customer ID&gt;</span>
              )}
            </div>
          </div>
          <div className={page.formGroup}>
            <label className={page.label}>
              Main DB name <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--aptean-text-muted)' }}>(auto-computed)</span>
            </label>
            <div className={page.vnetInfoBox}>
              {mainDb ? (
                <>
                  <span className={page.vnetName} style={{ fontFamily: 'monospace', fontSize: 13 }}>
                    {mainDb}
                  </span>
                  <span className={page.vnetHint}>Same as Customer ID</span>
                </>
              ) : (
                <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--aptean-text-muted)' }}>&lt;Customer ID&gt;</span>
              )}
            </div>
          </div>
        </div>
      )}

      {traverse ? (
        <>
          {asksCreateFor(data) && <CreatedForQuestion value={answers.createFor} onChange={(createFor) => onAnswersChange({ ...answers, createFor })} />}
          <ApplicationLicenseManagement
            answers={answers}
            onChange={onAnswersChange}
            sqlServer={dbServerAddress(data)}
            defaultDb={mainDb}
            tcpPort={tlsPort.trim()}
            productId={licenseProductId(data, answers)}
          />
          <ApplicationCreation answers={answers} onChange={onAnswersChange} inputs={scriptInputs(context, answers)} flags={flagValues(data, answers)} />
        </>
      ) : (
        asksLicense(data.product) && <LicenseFields />
      )}

      {problems.length > 0 && (
        <div aria-live="polite" style={{ marginTop: 16 }}>
          <span className={styles.status}>To continue:</span>
          <ul className={styles.problems}>
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
